import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const STREAK_MILESTONES = [3, 7, 14, 30];

const WelcomeHeader = ({ userName, xpPoints, dailyStreak, lastVisit, userId, dailyBonusClaimed, onStreakMilestone }) => {
  const navigate = useNavigate();
  const currentHour = new Date()?.getHours();
  const greeting = currentHour < 12 ? 'Good Morning' : currentHour < 18 ? 'Good Afternoon' : 'Good Evening';

  useEffect(() => {
    if (!dailyStreak || !userId) return;
    if (!STREAK_MILESTONES?.includes(dailyStreak) || !onStreakMilestone) return;

    const storageKey = `streak_milestone_shown_${userId}_${dailyStreak}`;
    if (localStorage.getItem(storageKey)) return;

    localStorage.setItem(storageKey, '1');
    const t = setTimeout(() => onStreakMilestone(dailyStreak), 600);
    return () => clearTimeout(t);
  }, [dailyStreak, userId, onStreakMilestone]);

  const handleEditClick = () => {
    navigate('/student-avatar-studio');
  };

  return (
    <div className="bg-gradient-to-br from-primary via-primary/90 to-secondary p-5 md:p-8 rounded-xl shadow-lg mb-6">
      <div className="flex flex-col gap-4">
        {/* Top row: Avatar + greeting */}
        <div className="flex items-center gap-4 md:gap-5">
          {userId && (
            <div className="flex-shrink-0">
              <Icon name="User" size={80} />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary-foreground mb-1 break-words">
              {greeting}, {userName}!
            </h1>
            <p className="text-primary-foreground/90 text-base md:text-lg">
              Ready to continue your Shakespeare journey?
            </p>
          </div>
        </div>

        {/* Bottom row: XP / Streak / Daily bonus */}
        <div className="flex flex-row flex-wrap gap-3">
          <div className="bg-primary-foreground/10 backdrop-blur-sm rounded-lg px-4 py-3 min-w-[120px] flex-1 sm:flex-none">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Zap" size={18} color="var(--color-secondary)" />
              <span className="text-xs font-medium text-primary-foreground/80">Total XP</span>
            </div>
            <p className="text-2xl md:text-3xl font-bold text-primary-foreground">{xpPoints?.toLocaleString()}</p>
          </div>

          <div className={`backdrop-blur-sm rounded-lg px-4 py-3 min-w-[120px] flex-1 sm:flex-none ${STREAK_MILESTONES?.includes(dailyStreak) ? 'bg-orange-500/20 ring-1 ring-orange-400/40' : 'bg-primary-foreground/10'}`}>
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Flame" size={18} color="var(--color-warning)" />
              <span className="text-xs font-medium text-primary-foreground/80">Day Streak</span>
              {STREAK_MILESTONES?.includes(dailyStreak) && (
                <span className="text-xs font-bold text-orange-300 animate-pulse">🎉</span>
              )}
            </div>
            <p className="text-2xl md:text-3xl font-bold text-primary-foreground">{dailyStreak}</p>
          </div>

          {dailyBonusClaimed && (
            <div className="bg-success/20 backdrop-blur-sm rounded-lg px-4 py-3 flex items-center gap-3 animate-pulse flex-1 sm:flex-none">
              <Icon name="Gift" size={22} color="var(--color-success)" />
              <div>
                <p className="text-sm font-medium text-success-foreground">Daily Bonus!</p>
                <p className="text-xs text-success-foreground/80">+10 XP earned</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomeHeader;